import { useState } from "react";
import type {
  ElementRoleRequest,
  PositionAnalysisResponse,
  PositionResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { HoverTip } from "@/components/ui/HoverTip";
import type { PositionActions } from "../positions/PositionRow";
import {
  ConfirmAgainstAdvice,
  warnsBeforeConfirming,
} from "./ConfirmAgainstAdvice";
import {
  type AcknowledgeGaps,
  acknowledge,
  ConfirmWithout,
} from "./MissingComparisons";
import { NEEDS_DECISION, needsDecision } from "./needs-decision";
import type { OverrideVerdict } from "./PositionRuling";

/**
 * What may be done to a position on the Novelty card: the list's acts, and the
 * three the card adds, confirming it, marking an element and ruling the
 * verdict. The card is the one place a position is confirmed.
 */
export interface AnalysisActions extends PositionActions {
  onConfirm: (positionId: string) => void;
  onMark: (
    positionId: string,
    elementId: string,
    mark: ElementRoleRequest | null,
  ) => Promise<unknown>;
  onOverride: OverrideVerdict;
}

/** Assess this position alone; while that run is in flight the button says so and is closed. */
function AssessButton({
  busy,
  assessing,
  onAssess,
}: {
  busy: boolean;
  assessing: boolean;
  onAssess: () => void;
}) {
  return (
    <Button
      type="button"
      size="dense"
      disabled={busy || assessing}
      onClick={onAssess}
      className="cursor-pointer"
    >
      {assessing ? "Assessing…" : "Assess novelty"}
    </Button>
  );
}

/**
 * Confirming a position, with whatever must be asked first: what it was not
 * compared with, when that holds it, carrying the advice in the same prompt;
 * otherwise the advice alone for an adverse verdict; otherwise nothing.
 */
function useConfirmFlow(
  position: PositionAnalysisResponse,
  actions: AnalysisActions,
  onAcknowledgeGaps: AcknowledgeGaps,
) {
  const [asking, setAsking] = useState<"gaps" | "advice" | null>(null);
  const confirm = () => {
    if (position.gaps_hold) setAsking("gaps");
    else if (warnsBeforeConfirming(position.verdict)) setAsking("advice");
    else actions.onConfirm(position.position_id);
  };
  const close = () => setAsking(null);
  const prompt =
    asking === "gaps" ? (
      <ConfirmWithout
        position={position}
        confirming
        onCancel={close}
        onGoOn={(reason) =>
          acknowledge(position, reason, onAcknowledgeGaps).then(() => {
            close();
            actions.onConfirm(position.position_id);
          })
        }
      />
    ) : asking === "advice" ? (
      <ConfirmAgainstAdvice
        verdict={position.verdict}
        onCancel={close}
        onConfirm={() => {
          close();
          actions.onConfirm(position.position_id);
        }}
      />
    ) : null;
  return { confirm, prompt };
}

/**
 * Confirm, or, while a Not novel verdict waits on a decision, Confirm held
 * with what would open it said beside it.
 */
function ConfirmAct({
  held,
  busy,
  onConfirm,
}: {
  held: boolean;
  busy: boolean;
  onConfirm: () => void;
}) {
  if (!held) {
    return (
      <Button
        type="button"
        size="dense"
        disabled={busy}
        variant="confirm"
        onClick={onConfirm}
      >
        Confirm
      </Button>
    );
  }
  return (
    <HoverTip tip={NEEDS_DECISION} wide={false}>
      {({ triggerProps }) => (
        <Button
          type="button"
          size="dense"
          variant="confirm"
          held
          {...triggerProps}
        >
          Confirm
        </Button>
      )}
    </HoverTip>
  );
}

/**
 * What may be done to the position beneath its detail: confirm, unless it is
 * confirmed already or not assessed; amend; remove. A yes on an adverse
 * verdict goes through the warning first.
 */
export function Actions({
  position,
  attorney,
  actions,
  busy,
  assessing,
  onAssess,
  onAcknowledgeGaps,
  onEdit,
  onOverride,
}: {
  position: PositionAnalysisResponse;
  attorney: PositionResponse;
  actions: AnalysisActions;
  busy: boolean;
  assessing: boolean;
  onAssess: (positionId: string) => void;
  onAcknowledgeGaps: AcknowledgeGaps;
  onEdit: () => void;
  /** Opens the override box, or nothing where there is no verdict to override. */
  onOverride: (() => void) | null;
}) {
  const flow = useConfirmFlow(position, actions, onAcknowledgeGaps);
  const unruled = position.verdict === "not_assessed";
  // A position no run has ruled is not confirmed, unless every comparison it was
  // asked for failed: then the acknowledgement is the way past, and once it
  // stands the yes is given on its own. The server says which, by the rule its
  // confirmation applies.
  const confirmable =
    !attorney.confirmed && (!unruled || position.failed_everywhere);
  return (
    <div className="flex items-center gap-2">
      {unruled && (
        <AssessButton
          busy={busy}
          assessing={assessing}
          onAssess={() => onAssess(position.position_id)}
        />
      )}
      {confirmable && (
        <ConfirmAct
          held={needsDecision(position, attorney)}
          busy={busy}
          onConfirm={flow.confirm}
        />
      )}
      <Button
        type="button"
        size="dense"
        variant="secondary"
        disabled={busy}
        onClick={onEdit}
        className="cursor-pointer"
      >
        Amend
      </Button>
      {onOverride !== null && (
        <Button
          type="button"
          size="dense"
          variant="secondary"
          disabled={busy}
          onClick={onOverride}
          className="cursor-pointer"
        >
          Override
        </Button>
      )}
      <Button
        type="button"
        size="dense"
        variant="danger-outline"
        disabled={busy}
        onClick={() => actions.onRemove(attorney)}
      >
        Remove
      </Button>
      {flow.prompt}
    </div>
  );
}
